import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
  connectFirestoreEmulator,
  getFirestore,
  type Firestore,
} from "firebase/firestore";
import {
  connectDatabaseEmulator,
  getDatabase,
  type Database,
} from "firebase/database";
import { captureException } from "./telemetry";

/**
 * Lazily initialize the Firebase app singleton from Vite env vars.
 *
 * If the required env vars are missing (e.g. local development without a
 * Firebase project, or a contributor cloning the repo for the first time),
 * we deliberately do NOT initialize the app — the rest of the codebase
 * checks {@link isFirebaseConfigured} and falls back to sensible defaults.
 */

interface FirebaseEnv {
  apiKey: string;
  authDomain?: string;
  projectId: string;
  appId: string;
  messagingSenderId?: string;
  storageBucket?: string;
  databaseURL?: string;
  /** GA4 stream measurement id, e.g. "G-XXXXXXXXXX". When present,
   *  `firebase/analytics` can initialize and the telemetry facade
   *  will route `track()` calls through Firebase Analytics. */
  measurementId?: string;
}

function readEnv(): FirebaseEnv | null {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY as string | undefined;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined;
  const appId = import.meta.env.VITE_FIREBASE_APP_ID as string | undefined;
  if (!apiKey || !projectId || !appId) return null;
  return {
    apiKey,
    projectId,
    appId,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as
      | string
      | undefined,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as
      | string
      | undefined,
    databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL as
      | string
      | undefined,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID as
      | string
      | undefined,
  };
}

let configured: boolean | null = null;
export function isFirebaseConfigured(): boolean {
  if (configured == null) configured = readEnv() !== null;
  return configured;
}

let cached: FirebaseApp | null = null;
export function getFirebaseApp(): FirebaseApp | null {
  if (cached) return cached;
  const env = readEnv();
  if (!env) return null;
  if (getApps().length > 0) {
    cached = getApp();
    return cached;
  }
  cached = initializeApp(env);
  return cached;
}

/** The region your callable functions run in. Override via env. */
export function getFunctionsRegion(): string {
  return (
    (import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined) ??
    "us-central1"
  );
}

let cachedFirestore: Firestore | null = null;
let firestoreEmulatorConnected = false;
let cachedDatabase: Database | null = null;
let databaseEmulatorConnected = false;
let warnedMissingDatabaseUrl = false;

/**
 * Lazily resolves the Firestore instance. Returns `null` when Firebase
 * isn't configured (so the cloud-sync code paths can no-op gracefully).
 *
 * The Firestore emulator is connected automatically when
 * `VITE_FIREBASE_USE_EMULATORS=true`.
 */
export function getFirebaseFirestore(): Firestore | null {
  if (cachedFirestore) return cachedFirestore;
  const app = getFirebaseApp();
  if (!app) return null;
  cachedFirestore = getFirestore(app);

  if (!firestoreEmulatorConnected) {
    const useEmulators =
      (import.meta.env.VITE_FIREBASE_USE_EMULATORS as string | undefined) ===
      "true";
    if (useEmulators) {
      try {
        connectFirestoreEmulator(cachedFirestore, "localhost", 8080);
      } catch {
        // Already connected — safe to ignore.
      }
      firestoreEmulatorConnected = true;
    }
  }

  return cachedFirestore;
}

/**
 * Lazily resolves the Realtime Database instance. Used for ephemeral
 * presence state in shared rooms (cursors, selection, drag previews) — see
 * {@link import("./presence").initPresence}.
 *
 * Returns `null` when Firebase isn't configured, or when no
 * `VITE_FIREBASE_DATABASE_URL` is provided. Both are valid degraded
 * states: shared rooms become "Firestore-only", which still works for
 * durable state but doesn't show live cursors.
 *
 * The RTDB emulator is connected automatically when
 * `VITE_FIREBASE_USE_EMULATORS=true`.
 */
export function getFirebaseDatabase(): Database | null {
  if (cachedDatabase) return cachedDatabase;
  const app = getFirebaseApp();
  if (!app) return null;

  const env = readEnv();
  // RTDB requires a databaseURL. Without one, return null so callers
  // degrade gracefully (shared rooms still load, just without cursors).
  if (!env?.databaseURL) {
    const useEmulators =
      (import.meta.env.VITE_FIREBASE_USE_EMULATORS as string | undefined) ===
      "true";
    if (!useEmulators) {
      // Firebase IS otherwise configured (we got an `app` above), so a
      // missing databaseURL is almost certainly a deploy/build-config
      // bug — VITE_FIREBASE_DATABASE_URL got dropped from the build's
      // env. Log loudly once so the regression is visible in devtools
      // instead of silently disabling multiplayer presence.
      if (!warnedMissingDatabaseUrl) {
        warnedMissingDatabaseUrl = true;
        const msg =
          "[firebase] VITE_FIREBASE_DATABASE_URL is not set in this build — " +
          "multiplayer presence (cursors, selection halos, drag previews) " +
          "is disabled. Add it to the deploy workflow's env block.";
        console.error(msg);
        captureException(new Error(msg), {
          context: "firebase.getFirebaseDatabase.missingDatabaseUrl",
        });
      }
      return null;
    }
  }

  try {
    cachedDatabase = getDatabase(app);
  } catch {
    return null;
  }

  if (!databaseEmulatorConnected) {
    const useEmulators =
      (import.meta.env.VITE_FIREBASE_USE_EMULATORS as string | undefined) ===
      "true";
    if (useEmulators) {
      try {
        connectDatabaseEmulator(cachedDatabase, "localhost", 9000);
      } catch {
        // Already connected — safe to ignore.
      }
      databaseEmulatorConnected = true;
    }
  }

  return cachedDatabase;
}
